import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  qrSvg: string;
  orderTitle: string;
  expiresAt?: string | null;
}

export const QRCodeModal = ({ isOpen, onClose, qrSvg, orderTitle, expiresAt }: QRCodeModalProps) => {
  const handleDownload = () => {
    // Convert data URL to blob for download
    const link = document.createElement('a');
    link.href = qrSvg; // PNG data URL
    link.download = `qr-code-${orderTitle}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base md:text-lg">Code QR de retrait</DialogTitle>
          <DialogDescription className="text-xs md:text-sm">
            Présentez ce code au point relais pour retirer votre commande
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-4 py-4">
          {/* QR Code avec meilleur affichage */}
          <div className="qr-code-container-buyer bg-white p-3 md:p-4 rounded-lg border-2 border-primary/20 shadow-sm">
            <img 
              src={qrSvg}
              alt="QR Code de retrait"
              className="w-full h-full object-contain"
              style={{ maxWidth: '280px', maxHeight: '280px' }}
            />
          </div>

          {/* Info visuelle colorée */}
          <div className="w-full bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-900 text-center font-medium">
              ✅ Commande prête pour retrait
            </p>
          </div>
          
          <div className="text-center space-y-2 w-full">
            <p className="text-sm md:text-base font-medium text-gray-900 line-clamp-2">{orderTitle}</p>
            {expiresAt && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2">
                <p className="text-xs md:text-sm text-orange-900">
                  ⏰ Valide jusqu'au {formatDate(expiresAt)}
                </p>
              </div>
            )}
          </div>

          {/* Instructions visuelles améliorées */}
          <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <p className="font-semibold mb-2 text-blue-900">📋 Instructions de retrait:</p>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">1.</span>
                <span>Présentez ce QR code au responsable du point relais</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">2.</span>
                <span>Il scannera le code pour confirmer votre identité</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">3.</span>
                <span>Vérifiez l'état du colis avant de partir</span>
              </li>
            </ul>
          </div>

          {/* Boutons d'action */}
          <div className="w-full flex flex-col sm:flex-row gap-2">
            <Button onClick={handleDownload} variant="outline" className="flex-1">
              <Download className="mr-2 h-4 w-4" />
              Télécharger
            </Button>
            <Button onClick={onClose} className="flex-1">
              Fermer
            </Button>
          </div>
        </div>

        <style>{`
          .qr-code-container-buyer {
            width: 100%;
            max-width: 300px;
            aspect-ratio: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};
